import { useState } from 'react'
import { X, Users, ChevronLeft, ChevronRight, Calendar, Info } from 'lucide-react'
import { useAppStore } from '@/lib/store'
import { formatRoomPrice, localizedName } from '@/lib/format'
import { parseAmenities } from '@/lib/amenities'
import type { LodgingRoomType } from '@/shared/types'

interface Props {
  roomType: LodgingRoomType
  onClose: () => void
  onBook: () => void
}

export function RoomDetailsModal({ roomType, onClose, onBook }: Props) {
  const lang = useAppStore((s) => s.lang)

  const photos = roomType.photos?.length
    ? roomType.photos
    : roomType.photo_url
      ? [roomType.photo_url]
      : []

  const [photoIndex, setPhotoIndex] = useState(0)

  const name = localizedName(lang, roomType.name_uk, roomType.name_en, roomType.name_ru)
  const description = localizedName(lang, roomType.description_uk, roomType.description_en, roomType.description_ru)
  const amenities = parseAmenities(roomType.amenities)

  const t = {
    title: lang === 'en' ? 'Room Details' : lang === 'ru' ? 'Характеристики номера' : 'Характеристики номера',
    capacity: lang === 'en' ? `Up to ${roomType.capacity} guests` : lang === 'ru' ? `До ${roomType.capacity} гостей` : `До ${roomType.capacity} гостей`,
    priceFrom: lang === 'en' ? 'from' : lang === 'ru' ? 'от' : 'від',
    perNight: lang === 'en' ? '/ night' : lang === 'ru' ? '/ ночь' : '/ ніч',
    amenitiesTitle: lang === 'en' ? 'Amenities & Features' : lang === 'ru' ? 'Удобства и оснащение' : 'Зручності та оснащення',
    descriptionTitle: lang === 'en' ? 'Description' : lang === 'ru' ? 'Описание' : 'Опис',
    minStay: lang === 'en' ? `Minimum stay: ${roomType.min_stay_nights} night(s)` : lang === 'ru' ? `Мин. бронирование: ${roomType.min_stay_nights} ноч.` : `Мін. бронювання: ${roomType.min_stay_nights} ноч.`,
    bookNow: lang === 'en' ? 'Book Room' : lang === 'ru' ? 'Забронировать номер' : 'Забронювати номер',
    close: lang === 'en' ? 'Close' : lang === 'ru' ? 'Закрыть' : 'Закрити',
  }

  const prevPhoto = () => {
    if (photos.length <= 1) return
    setPhotoIndex((i) => (i === 0 ? photos.length - 1 : i - 1))
  }

  const nextPhoto = () => {
    if (photos.length <= 1) return
    setPhotoIndex((i) => (i === photos.length - 1 ? 0 : i + 1))
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-200">
      <div className="relative w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden rounded-3xl border border-slate-800 bg-slate-900 shadow-2xl text-slate-100">
        
        {/* Header Bar */}
        <div className="absolute top-4 right-4 z-20">
          <button
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-950/70 text-slate-200 backdrop-blur-md transition hover:bg-slate-950 hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto overflow-x-hidden p-0">
          
          {/* Photo Gallery */}
          <div className="relative h-64 sm:h-80 w-full bg-slate-950">
            {photos.length > 0 ? (
              <img
                src={photos[photoIndex]}
                alt={name}
                className="h-full w-full object-cover transition-all duration-300"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-slate-600">
                <Info className="h-12 w-12" />
              </div>
            )}

            {photos.length > 1 && (
              <>
                <button
                  onClick={prevPhoto}
                  className="absolute left-3 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full bg-slate-950/60 text-white backdrop-blur-md transition hover:bg-slate-950"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={nextPhoto}
                  className="absolute right-3 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full bg-slate-950/60 text-white backdrop-blur-md transition hover:bg-slate-950"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 rounded-full bg-slate-950/50 px-3 py-1 backdrop-blur-md">
                  {photos.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setPhotoIndex(idx)}
                      className={`h-2 rounded-full transition-all ${
                        idx === photoIndex ? 'w-5 bg-indigo-500' : 'w-2 bg-slate-500/60'
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Details Body */}
          <div className="p-6 space-y-6">
            
            {/* Title & Price Header */}
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 border-b border-slate-800 pb-5">
              <div>
                <span className="inline-flex items-center gap-1 rounded-full bg-indigo-500/10 px-3 py-1 text-xs font-semibold text-indigo-400">
                  <Users className="h-3.5 w-3.5" />
                  {t.capacity}
                </span>
                <h2 className="mt-2 text-2xl font-bold text-white">{name}</h2>
              </div>
              <div className="text-left sm:text-right">
                <span className="text-xs uppercase tracking-wider text-slate-400">{t.priceFrom}</span>
                <div className="text-2xl font-extrabold text-indigo-400">
                  {formatRoomPrice(roomType.base_price_uah)}{' '}
                  <span className="text-sm font-normal text-slate-400">{t.perNight}</span>
                </div>
              </div>
            </div>

            {/* Key Specs Grid (Area, Beds, Floor, Capacity) */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 border-b border-slate-800 pb-5">
              <div className="rounded-2xl border border-slate-800 bg-slate-950 p-3 text-center">
                <span className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  {lang === 'en' ? 'Area' : lang === 'ru' ? 'Площадь' : 'Площа'}
                </span>
                <span className="mt-1 block text-base font-bold text-slate-100">
                  {roomType.room_area_sqm || 20} м²
                </span>
              </div>

              <div className="rounded-2xl border border-slate-800 bg-slate-950 p-3 text-center">
                <span className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  {lang === 'en' ? 'Guests' : lang === 'ru' ? 'Гости' : 'Гості'}
                </span>
                <span className="mt-1 block text-base font-bold text-slate-100 flex items-center justify-center gap-1">
                  <Users className="h-4 w-4 text-indigo-400 inline" /> {roomType.capacity}
                </span>
              </div>

              <div className="rounded-2xl border border-slate-800 bg-slate-950 p-3 text-center">
                <span className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  {lang === 'en' ? 'Beds' : lang === 'ru' ? 'Кровати' : 'Ліжка'}
                </span>
                <span className="mt-1 block text-xs font-bold text-slate-100 truncate" title={localizedName(lang, roomType.bed_type_text_uk ?? '', roomType.bed_type_text_en ?? '', roomType.bed_type_text_ru ?? '')}>
                  {localizedName(lang, roomType.bed_type_text_uk ?? '', roomType.bed_type_text_en ?? '', roomType.bed_type_text_ru ?? '') || (roomType.capacity === 4 ? '1 кровать + 1 диван' : '2 кровати')}
                </span>
              </div>

              <div className="rounded-2xl border border-slate-800 bg-slate-950 p-3 text-center">
                <span className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  {lang === 'en' ? 'Floor' : lang === 'ru' ? 'Этаж' : 'Поверх'}
                </span>
                <span className="mt-1 block text-xs font-bold text-slate-100 truncate">
                  {localizedName(lang, roomType.floor_text_uk ?? '', roomType.floor_text_en ?? '', roomType.floor_text_ru ?? '') || '2 из 3'}
                </span>
              </div>
            </div>

            {/* Description */}
            {description && (
              <div>
                <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">
                  {t.descriptionTitle}
                </h3>
                <p className="text-sm leading-relaxed text-slate-300 whitespace-pre-line">
                  {description}
                </p>
              </div>
            )}

            {/* Amenities Grid */}
            {amenities.length > 0 && (
              <div>
                <h3 className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-400">
                  {t.amenitiesTitle}
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                  {amenities.map((item) => {
                    const Icon = item.icon
                    return (
                      <div
                        key={item.code}
                        className="flex items-center gap-2.5 rounded-xl border border-slate-800 bg-slate-950/70 p-3 text-xs text-slate-200"
                      >
                        <Icon className="h-4 w-4 shrink-0 text-indigo-400" />
                        <span className="font-medium truncate">{item.label[lang] || item.label.uk}</span>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Minimum Stay notice */}
            {Boolean(roomType.min_stay_nights && roomType.min_stay_nights > 1) && (
              <div className="flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-950 p-3 text-xs text-slate-400">
                <Calendar className="h-4 w-4 text-indigo-400 shrink-0" />
                <span>{t.minStay}</span>
              </div>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="border-t border-slate-800 bg-slate-900/95 p-4 backdrop-blur-md flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 rounded-xl border border-slate-700 py-3 text-sm font-semibold text-slate-300 hover:bg-slate-800 transition"
          >
            {t.close}
          </button>
          <button
            onClick={() => {
              onClose()
              onBook()
            }}
            className="flex-[2] rounded-xl bg-indigo-600 py-3 text-sm font-bold text-white shadow-lg shadow-indigo-600/25 transition hover:bg-indigo-500"
          >
            {t.bookNow}
          </button>
        </div>

      </div>
    </div>
  )
}
